import i18n from "@/utils/i18n";
import { apiClient } from "@/lib/apiClient";
import type { AdminUserListItem, AdminUserDetail } from "@/lib/users.api";
import {
  getAdminUsers,
  getAdminUserById,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  bulkImportAdminUsers,
  getUserRoles,
  assignUserRole,
  revokeUserRole,
  getAdminLocations,
  getAdminCompanies,
  getAdminDepartments,
  getAdminRootDepartments,
  getAdminDomains,
} from "@/lib/users.api";
import type { AdminUserCreateBody } from "@/lib/users.api";
import type { AdminUsersListParams } from "@/lib/users.api";
import { logsApi, formatLogDateTime } from "@/features/logs/services/logs.api";
import type { ClientEducationCertificatesApiResponse } from "@/features/client-certificates/types/client-certificates.types";
import type { Staff, StaffFilters } from "../types/staff-management.types";
import { normalizeStaffGender } from "../utils/normalizeStaffGender";
import type {
  PersonnelTrainingRow,
  PersonnelExamRow,
  PersonnelCertificateRow,
  PersonnelLogRow,
} from "../types/staff-management.types";

function mapUserToStaff(user: AdminUserListItem | AdminUserDetail): Staff {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || "—";
  const companyName =
    typeof user.company === "object" && user.company != null
      ? user.company.name
      : (user.company as string | undefined);
  const locationName =
    typeof user.location === "object" && user.location != null
      ? user.location.name
      : (user.location as string | undefined);
  const departmentName =
    typeof user.department === "object" && user.department != null
      ? user.department.name
      : (user.department as string | undefined);
  const completedCount =
    user.completedEducationsCount ??
    user.completedTrainings ??
    0;
  const companyId =
    typeof user.company === "object" && user.company != null
      ? user.company.id
      : undefined;
  const locationId =
    typeof user.location === "object" && user.location != null
      ? user.location.id
      : undefined;
  const departmentId =
    typeof user.department === "object" && user.department != null
      ? user.department.id
      : undefined;
  const rootDepartmentName = user.rootDepartment?.name;
  const rootDepartmentId = user.rootDepartment?.id;

  return {
    id: user.id,
    staffId: user.identityNumber ?? user.id,
    name,
    title: user.title ?? "—",
    department: departmentName ?? "—",
    rootDepartment: rootDepartmentName,
    company: companyName ?? "—",
    location: locationName ?? "—",
    departmentId,
    rootDepartmentId,
    companyId,
    locationId,
    completedEducationsCount: completedCount,
    takenEducationsCount: user.takenEducationsCount ?? undefined,
    certificatesCount: user.certificatesCount ?? undefined,
    absencesCount: user.absencesCount ?? undefined,
    email: user.email,
    phoneNumber: user.phoneNumber,
    identityNumber: user.identityNumber,
    ldapUsername: user.ldapUsername ?? null,
    status: user.isActive != null ? (user.isActive ? "active" : "inactive") : (user as unknown as Record<string, unknown>).status as string | undefined,
    roleId: user.roles?.[0]?.id ?? (user as unknown as Record<string, unknown>).roleId as string | undefined,
    roleName: user.roles?.[0]?.name ?? (user as unknown as Record<string, unknown>).roleName as string | undefined,
    createdAt: user.createdAt,
    isBanned: (user as AdminUserListItem & { isBanned?: boolean }).isBanned,
    gender: normalizeStaffGender(
      (user as AdminUserListItem & { gender?: unknown }).gender,
    ),
  };
}

export const staffManagementApi = {
  /**
   * GET /api/admin/users - List users with pagination, filtering, sorting.
   */
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    filters?: StaffFilters;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: Staff[]; total: number }> => {
    const apiParams: AdminUsersListParams = {
      page: params?.page ?? 1,
      limit: params?.pageSize ?? 10,
      search: params?.filters?.search,
      status: params?.filters?.status,
      roleId: params?.filters?.roleId,
      role: params?.filters?.role,
      locationId: params?.filters?.locationId ?? params?.filters?.location,
      companyId: params?.filters?.companyId ?? params?.filters?.company,
      departmentId: params?.filters?.departmentId ?? params?.filters?.department,
      rootDepartmentId: params?.filters?.rootDepartmentId,
      title: params?.filters?.title,
      identityNumber: params?.filters?.tcNo,
      name: params?.filters?.name,
      joinedAt: params?.filters?.joinedAt,
      minTenureYears: params?.filters?.minTenureYears,
      groupId: params?.filters?.groupId,
      sortBy: params?.sortBy ?? "createdAt",
      sortOrder: params?.sortOrder ?? "desc",
    };
    const { data, total } = await getAdminUsers(apiParams);
    return { data: data.map(mapUserToStaff), total };
  },

  /**
   * GET /api/admin/users/:id - Get single user by ID.
   */
  getById: async (id: string): Promise<Staff> => {
    const user = await getAdminUserById(id);
    return mapUserToStaff(user);
  },

  /**
   * POST /api/admin/users - Create new user.
   */
  create: async (body: AdminUserCreateBody): Promise<Staff> => {
    const user = await createAdminUser(body);
    return mapUserToStaff(user);
  },

  /**
   * PATCH /api/admin/users/:id — kısmi güncelleme (ör. şifre: `{ password }`).
   */
  update: async (
    id: string,
    body: {
      firstName?: string;
      lastName?: string;
      email?: string;
      identityNumber?: string;
      phoneNumber?: string;
      title?: string;
      ldapUsername?: string;
      departmentId?: string;
      companyId?: string;
      locationId?: string;
      status?: string;
      gender?: "male" | "female";
      password?: string;
    },
  ): Promise<Staff> => {
    const user = await updateAdminUser(id, body);
    return mapUserToStaff(user);
  },

  /**
   * DELETE /api/admin/users/:id - Soft-delete user.
   */
  delete: (id: string): Promise<void> => deleteAdminUser(id),

  /**
   * POST /api/admin/users/bulk-import - Bulk import from Excel.
   */
  bulkImport: (file: File) => bulkImportAdminUsers(file),

  /**
   * GET /api/admin/users/:id/roles - Get user's roles.
   */
  getRoles: getUserRoles,

  /**
   * PUT /api/admin/users/:id/roles - Set user roles (full replace; used for both add and update).
   */
  assignRole: assignUserRole,

  /**
   * DELETE /api/admin/users/:id/roles/:roleId - Revoke role from user.
   */
  revokeRole: revokeUserRole,

  /**
   * GET /api/admin/locations - List locations for filters.
   */
  getLocations: getAdminLocations,

  /**
   * GET /api/admin/companies - List companies for filters.
   */
  getCompanies: getAdminCompanies,

  /**
   * GET /api/admin/departments - List departments for filters.
   */
  getDepartments: getAdminDepartments,

  /**
   * GET /api/admin/departments/roots - List only root departments (kök Başkanlık / Santral).
   */
  getRootDepartments: getAdminRootDepartments,

  /**
   * GET /api/admin/domains - List domains for user creation (domainId required).
   */
  getDomains: getAdminDomains,

  /**
   * Personel detay sayfası: Kullanıcıya ait eğitim kayıtları.
   * GET /api/admin/educations/education-enrollments/users/:userId (filtrelerde userId path'te)
   */
  getPersonnelTrainings: async (
    userId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      /** education.type enum (0: Yüz Yüze, 1: Canlı, 2/3: Üniteli) */
      type?: number;
      /** education.status enum (0: REQUEST_COLLECTION, 1: BUDGET_PENDING, 2: ACTIVE, 3: CLOSED, 4: ARCHIVED) */
      status?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }
  ): Promise<{ data: PersonnelTrainingRow[]; total: number }> => {
    const query: Record<string, unknown> = {};
    if (params?.page != null) query.page = params.page;
    if (params?.limit != null) query.limit = params.limit;
    if (params?.search) query.search = params.search;
    if (params?.type != null) query.type = params.type;
    if (params?.status != null) query.status = params.status;
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;

    try {
      const { data } = await apiClient.get<{
        data?: Array<{
          id?: string;
          educationID?: string;
          education?: { name?: string; type?: number; format?: string };
          progressPercent?: number;
          status?: number;
          [key: string]: unknown;
        }>;
        count?: number;
        total?: number;
      }>(`/api/admin/educations/education-enrollments/users/${userId}`, {
        params: Object.keys(query).length > 0 ? query : undefined,
      });
      const items = data?.data ?? [];
      const total = data?.count ?? data?.total ?? items.length;
      const statusToLabel: Record<number, { label: string; type: PersonnelTrainingRow["statusType"] }> = {
        0: { label: i18n.t("staffManagement.trainingStatusLabels.requested"), type: "info" },
        1: { label: i18n.t("staffManagement.trainingStatusLabels.approvalPending"), type: "warning" },
        2: { label: i18n.t("staffManagement.trainingStatusLabels.inProgress"), type: "info" },
        3: { label: i18n.t("staffManagement.trainingStatusLabels.rejected"), type: "error" },
        4: { label: i18n.t("staffManagement.trainingStatusLabels.rejected"), type: "error" },
        5: { label: i18n.t("staffManagement.trainingStatusLabels.cancelled"), type: "default" },
        6: { label: i18n.t("staffManagement.trainingStatusLabels.completed"), type: "success" },
        11: { label: i18n.t("clientEducations.status.notCompleted"), type: "error" },
      };
      const typeToFormat: Record<number, string> = {
        0: i18n.t("requestAndApproval.educationTypes.faceToFace"),
        1: i18n.t("requestAndApproval.educationTypes.online"),
        2: i18n.t("requestAndApproval.educationTypes.unit"),
        3: i18n.t("requestAndApproval.educationTypes.unit"),
      };
      const rows: PersonnelTrainingRow[] = items.map((item) => {
        const statusNum = item.status ?? 0;
        const st = statusToLabel[statusNum] ?? { label: "—", type: "default" as const };
        const progress = Math.min(100, Math.round(item.progressPercent ?? 0));
        const formatNum = item.education?.type ?? 2;
        const educationId = item.educationID ?? (item as { education?: { id?: string } }).education?.id ?? "";
        return {
          id: item.id ?? "",
          educationId,
          trainingName: item.education?.name ?? "—",
          format: typeToFormat[formatNum] ?? item.education?.format ?? "—",
          progress,
          status: progress >= 100 ? i18n.t("staffManagement.trainingStatusLabels.completed") : st.label,
          statusType: progress >= 100 ? "success" : st.type,
        };
      });
      return { data: rows, total };
    } catch {
      return { data: [], total: 0 };
    }
  },

  /**
   * Personel detay sayfası: Kullanıcıya ait sınav denemeleri.
   * GET /api/admin/exams/attempts?userId=xxx (veya examId ile filtre için examId=)
   */
  getPersonnelExams: async (
    userId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      examId?: string;
    }
  ): Promise<{ data: PersonnelExamRow[]; total: number }> => {
    const query: Record<string, unknown> = { userId };
    if (params?.examId) query.examId = params.examId;
    if (params?.page != null) query.page = params.page;
    if (params?.limit != null) query.limit = params.limit;
    if (params?.search) query.search = params.search;
    if (params?.status) query.status = params.status;
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;

    try {
      const { data } = await apiClient.get<{
        data?: Array<{
          id?: string;
          examId?: string;
          examID?: string;
          exam_id?: string;
          exam?: { id?: string; name?: string };
          examName?: string;
          score?: number;
          status?: number;
          [key: string]: unknown;
        }>;
        count?: number;
        total?: number;
      }>("/api/admin/exams/attempts", { params: query });
      const items = data?.data ?? [];
      const total = data?.count ?? data?.total ?? items.length;
      const statusToLabel: Record<number, { label: string; type: PersonnelExamRow["statusType"] }> = {
        0: { label: i18n.t("staffManagement.examStatusLabels.notAttended"), type: "default" },
        1: { label: i18n.t("staffManagement.examStatusLabels.passed"), type: "success" },
        2: { label: i18n.t("staffManagement.examStatusLabels.failed"), type: "error" },
      };
      const rows: PersonnelExamRow[] = items.map((item) => {
        const statusNum = item.status ?? 0;
        const st =
          statusToLabel[statusNum] ??
          ({ label: "—", type: "default" } as const);
        const examName = item.examName ?? item.exam?.name ?? "—";
        const score = item.score != null ? String(item.score) : "—";
        const raw = item as Record<string, unknown> & {
          exam?: { id?: string };
          examId?: string;
          examID?: string;
          exam_id?: string;
        };

        // Exam Detay drawer'ı, gerçek sınav ID'si ile çalışıyor.
        // Önce nested exam.id'yi tercih et, sonra flat alanlara düş.
        const examId =
          (raw.exam &&
            typeof raw.exam === "object" &&
            typeof (raw.exam as { id?: string }).id === "string"
            ? (raw.exam as { id: string }).id
            : null) ??
          (typeof raw.examId === "string" ? raw.examId : null) ??
          (typeof raw.examID === "string" ? raw.examID : null) ??
          (typeof raw.exam_id === "string" ? raw.exam_id : null) ??
          (typeof (raw as Record<string, unknown>).ExamId === "string"
            ? ((raw as Record<string, unknown>).ExamId as string)
            : null) ??
          "";

        return {
          id: item.id ?? "",
          examId,
          examName,
          score,
          status: st.label,
          statusType: st.type,
        };
      });
      return { data: rows, total };
    } catch {
      return { data: [], total: 0 };
    }
  },

  /**
   * Personel detay sayfası: Kullanıcıya ait sertifikalar.
   * GET /api/client/education-certificates/{userId}
   */
  getPersonnelCertificates: async (
    userId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }
  ): Promise<{ data: PersonnelCertificateRow[]; total: number }> => {
    const query: Record<string, unknown> = {};
    if (params?.page != null) query.page = params.page;
    if (params?.limit != null) query.limit = params.limit;
    if (params?.search) query.search = params.search;
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortOrder) query.sortOrder = params.sortOrder;

    try {
      const { data } = await apiClient.get<ClientEducationCertificatesApiResponse>(
        `/api/client/education-certificates/${userId}`,
        {
          params: Object.keys(query).length > 0 ? query : undefined,
        },
      );

      const items = Array.isArray(data?.data) ? data.data : [];
      const total = data?.count ?? items.length;

      const rows: PersonnelCertificateRow[] = items.map((item) => {
        const issuedAt = item.issuedAt ?? item.createdAt;
        const created = issuedAt
          ? new Date(issuedAt).toLocaleDateString("tr-TR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "—";

        return {
          id:
            item.certificateId ??
            item.enrollmentId ??
            item.id ??
            "",
          name:
            item.certificateName ??
            item.title ??
            item.education?.name ??
            item.educationName ??
            "—",
          relatedEducation:
            item.education?.name ?? item.educationName ?? "—",
          issueCount:
            (item as any).issuedClientCount != null
              ? String((item as any).issuedClientCount)
              : "1",
          createdAt: created,
          parsedHtml: item.parsedHtml ?? undefined,
          enrollmentId:
            item.enrollmentId ??
            item.education?.enrollment?.id ??
            undefined,
          educationId: item.educationId ?? item.education?.id ?? undefined,
          certificateNo:
            item.certificateNumber ?? item.certificateNo ?? undefined,
          imageUrl: item.fileUrl ?? item.imageUrl ?? undefined,
        };
      });

      return { data: rows, total };
    } catch {
      return { data: [], total: 0 };
    }
  },

  /**
   * Personel detay sayfası: Kullanıcıya ait audit loglar.
   * GET /api/admin/audit-logs?userID=xxx (filtrelerde userID otomatik gider)
   */
  getPersonnelLogs: async (
    userId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }
  ): Promise<{ data: PersonnelLogRow[]; total: number }> => {
    const { data, total } = await logsApi.getAll({
      userID: userId,
      page: params?.page ?? 1,
      limit: params?.limit ?? 10,
      search: params?.search,
      sortBy: (params?.sortBy ?? "createdAt") as "createdAt",
      sortOrder: params?.sortOrder ?? "desc",
    });
    const rows: PersonnelLogRow[] = data.map((log) => ({
      id: log.id,
      dateTime: formatLogDateTime(log.createdAt),
      title: log.module ?? log.endpointName ?? "—",
      description: log.action ?? log.errorMessage ?? "—",
      badge: log.status ?? "—",
    }));
    return { data: rows, total };
  },
};
